import { useState } from 'react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/lib/utils';

const NewsletterSignup = ({ source = 'footer', variant = 'full' }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email, source });
      if (res.data.already_subscribed) {
        toast.info("You're already subscribed!");
      } else {
        toast.success('Welcome! You\'re now subscribed.');
      }
      setSubscribed(true);
      setEmail('');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'brand-story') {
    return (
      <div data-testid="newsletter-brand-story" className="mt-8 max-w-md">
        {subscribed ? (
          <div className="flex items-center gap-2 text-gold" data-testid="newsletter-success-brand-story">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">You&apos;re on the list — watch your inbox.</span>
          </div>
        ) : (
          <>
            <p className="text-gold font-accent tracking-[0.2em] text-xs mb-2">STAY IN THE LOOP</p>
            <h3 className="font-display text-xl md:text-2xl font-semibold text-white mb-2">
              Get notified when new builds drop
            </h3>
            <p className="text-white/60 text-sm mb-4">
              One email when we finish a custom build. No spam, unsubscribe anytime.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input-brand-story"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-brand-story"
                className="bg-gold text-navy hover:bg-gold-hover h-11 px-6 whitespace-nowrap font-medium"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Notify Me
              </Button>
            </form>
          </>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div data-testid="newsletter-signup-compact">
        <h4 className="font-display text-sm font-semibold text-white uppercase tracking-wider mb-4">
          Newsletter
        </h4>
        <p className="text-white/60 text-sm mb-3">
          Get van insights & exclusive offers.
        </p>
        {subscribed ? (
          <div className="flex items-center gap-2 text-gold text-sm" data-testid="newsletter-success-compact">
            <CheckCircle className="w-4 h-4" /> Subscribed!
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              data-testid="newsletter-email-input-compact"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 text-sm h-9"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-compact"
              className="bg-gold text-navy hover:bg-gold-hover h-9 px-3 text-sm flex-shrink-0"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            </Button>
          </form>
        )}
      </div>
    );
  }

  return (
    <section className="bg-navy py-16" data-testid="newsletter-signup-full">
      <div className="container mx-auto px-6 lg:px-12 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
          Stay Updated
        </h2>
        <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
          Get the latest insights on custom vans, industry news, and exclusive offers delivered to your inbox.
        </p>
        {subscribed ? (
          <div className="flex items-center justify-center gap-3 text-gold text-lg" data-testid="newsletter-success-full">
            <CheckCircle className="w-6 h-6" />
            <span className="font-medium">You&apos;re subscribed! Check your inbox for updates.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-email-input"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="bg-gold text-navy hover:bg-gold-hover whitespace-nowrap"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Subscribe
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

export default NewsletterSignup;
